import { useTranslation } from 'react-i18next';
import type { OccurrenceResponse } from '@kidshome/api-contract';
import { CoinIcon, MissionCover, StatePill } from '@/shared/ui';
import { CheckGlyph, CameraGlyph, RedoGlyph } from '@/shared/ui/kid-chore-glyphs';
import { isChoreDoneByChild, canUndoByChild } from '@/shared/lib/chore-status';
import { PhotoCompleteButton } from '@/features/photo-complete-task';

/**
 * A single mission as a COVER TILE (ADR-0141) — the shelf/gallery unit, in contrast to the day board's
 * `OccCard` row. Every tile is the SAME shape: a square cover (the guardian's photo or the icon glyph,
 * always cropped to the square), then a two-line title and a footer row that pins a coin chip and ONE
 * round control to the bottom — so icon tiles and photo tiles are pixel-consistent and nothing overflows
 * the card. The control is always a 44px circle (never a wide text button, which used to spill out of the
 * narrow tile): a check to complete / undo, a camera for a photo-proof mission (tap opens the detail to
 * add the photo), or a retry for a rejected one. Missions never go `missed`/`skipped`.
 */
export function MissionTile({
  occ,
  canComplete,
  completing,
  redoing,
  undoing,
  onComplete,
  onRedo,
  onUndo,
  onOpen,
}: {
  occ: OccurrenceResponse;
  canComplete: boolean;
  completing: boolean;
  redoing: boolean;
  undoing: boolean;
  onComplete: () => void;
  onRedo: () => void;
  onUndo: () => void;
  /** Open the mission detail. Undefined for a PROJECTED (virtual) mission — no record to open yet. */
  onOpen: (() => void) | undefined;
}): React.ReactElement {
  const { t } = useTranslation('family');
  const done = isChoreDoneByChild(occ.status);
  const showUndo = done && canUndoByChild(occ);

  const control = (() => {
    if (!canComplete) return null;
    if (occ.status === 'rejected') {
      // A rejected photo mission needs a fresh photo (detail); a plain one re-submits inline. Both read
      // as one warm round "try again" control — camera when a photo is required, else a redo arrow.
      return occ.requiresPhotoProof ? (
        <button
          type="button"
          className="check cam"
          aria-label={t('choreDetail.tryAgain')}
          title={t('choreDetail.tryAgain')}
          onClick={onOpen}
        >
          <CameraGlyph />
        </button>
      ) : (
        <button
          type="button"
          className="check redo"
          disabled={redoing}
          aria-label={t('choreDetail.tryAgain')}
          title={t('choreDetail.tryAgain')}
          onClick={onRedo}
        >
          <RedoGlyph />
        </button>
      );
    }
    if (occ.requiresPhotoProof && occ.status === 'planned') {
      // A planned photo-proof mission is a COMPUTED virtual card (ADR-0142) — no occurrence to open yet.
      // The camera captures + completes in one step, addressed by `(templateId, localDate)` (F8).
      return (
        <PhotoCompleteButton
          card={{
            templateId: occ.templateId,
            scheduledLocalDate: occ.scheduledLocalDate,
            assigneeId: occ.assigneeId,
            title: occ.title,
          }}
          ctx={{
            date: occ.scheduledLocalDate,
            assigneeId: occ.assigneeId,
            childId: occ.assigneeId,
          }}
        />
      );
    }
    if (done) {
      // The green check itself is the undo toggle (tap to un-do), like a checkbox — no separate link
      // (ADR-0141). A grown-up-approved mission isn't kid-undoable, so its check is a static indicator.
      return showUndo ? (
        <button
          type="button"
          className={`check is-done${undoing ? ' is-pending' : ''}`}
          disabled={undoing}
          aria-pressed={true}
          aria-label={t('today.undoDoneAriaLabel', { chore: occ.title })}
          title={t('today.undo')}
          onClick={onUndo}
        >
          <CheckGlyph />
        </button>
      ) : (
        <span className="check is-done" role="img" aria-label={t('status.done')}>
          <CheckGlyph />
        </span>
      );
    }
    return (
      <button
        type="button"
        className={`check${completing ? ' is-pending' : ''}`}
        disabled={completing}
        aria-label={t('today.markDoneAriaLabel', { chore: occ.title })}
        title={t('today.markDone')}
        onClick={onComplete}
      >
        <CheckGlyph faint />
      </button>
    );
  })();

  // A mission that's finished the kid's part but ISN'T truly done yet needs a label so the kid doesn't
  // read the tile as "all done": `pending_approval` is waiting on a grown-up (nothing credited yet),
  // `rejected` needs another go. Truly-done (`approved` / same-day auto-credited) + `planned` speak for
  // themselves through the control, so they carry no ribbon. Mirrors the day board's inline StatePill.
  const statusRibbon =
    occ.status === 'pending_approval' || occ.status === 'rejected' ? (
      <div className="pointer-events-none absolute inset-x-0 top-0 flex p-2">
        <StatePill status={occ.status} className="max-w-full px-2 py-0.5 text-[11px] shadow-sm" />
      </div>
    ) : null;

  return (
    <div className="flex flex-col overflow-hidden rounded-card border border-border bg-surface">
      {/* The cover is a hard square: a `relative aspect-square` box with the image/glyph absolutely
          filling it, so a portrait photo is cropped to the same square as an icon tile (never taller).
          A status ribbon overlays the top for the "not-done-yet" states (waiting / try again). */}
      <div className="relative">
        {onOpen ? (
          <button
            type="button"
            onClick={onOpen}
            aria-label={occ.title}
            className="relative block aspect-square w-full overflow-hidden focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary"
          >
            <MissionCover
              icon={occ.icon}
              coverPhotoKey={occ.coverPhotoKey}
              className="absolute inset-0 size-full"
              rounded="rounded-none"
              glyphSize={72}
            />
          </button>
        ) : (
          // A PROJECTED (virtual) mission has no detail record to open yet — render an inert cover.
          <div className="relative block aspect-square w-full overflow-hidden">
            <MissionCover
              icon={occ.icon}
              coverPhotoKey={occ.coverPhotoKey}
              className="absolute inset-0 size-full"
              rounded="rounded-none"
              glyphSize={72}
            />
          </div>
        )}
        {statusRibbon}
      </div>
      {/* A fixed-shape footer: a two-line title (reserved height, so a short and a long title tile match)
          over a bottom row that pins the coin chip + the round control — identical across every tile. */}
      <div className="flex flex-1 flex-col gap-2 p-2.5">
        <span className="line-clamp-2 min-h-[2.55em] text-sm font-semibold leading-snug text-text">
          {occ.title}
        </span>
        <div className="mt-auto flex items-center gap-2">
          {occ.coinValue > 0 && (
            <span
              className="occ-coin"
              role="img"
              aria-label={t('common.coinsLabel', { count: occ.coinValue })}
            >
              <CoinIcon />
              {occ.coinValue}
            </span>
          )}
          {control && <div className="ms-auto">{control}</div>}
        </div>
      </div>
    </div>
  );
}
